import { Component, inject, OnInit, signal } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { Provider } from "@supabase/supabase-js";
import { TurnstileComponent } from "src/app/core/ui/turnstile/turnstile.component";
import { ClassService, InviteDetails } from "../../../features/classes/services/class.service";
import { AuthProvider } from "../../services/auth/auth.interface";
import { AuthService } from "../../services/auth/auth.service";
import { SUBSCRIPTION_MODULE_CONFIG } from "../../subscription.token";

const INVITE_TOKEN_KEY = 'classjax_invite_token';

@Component({
    selector: "app-signup",
    standalone: true,
    imports: [ReactiveFormsModule, RouterModule, TurnstileComponent],
    templateUrl: "./signup.component.html",
    styleUrl: "./signup.component.scss"
})
export class SignupPageComponent implements OnInit {
  private fb = inject(FormBuilder);
  private auth = inject(AuthService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private classService = inject(ClassService);
  private moduleConfig = inject(SUBSCRIPTION_MODULE_CONFIG);

  providers: AuthProvider[] = this.moduleConfig.auth.providers;
  busy = false;
  error?: string;
  showMailConfirm = false;

  // Invite context
  inviteToken = signal<string | null>(null);
  inviteDetails = signal<InviteDetails | null>(null);
  emailLocked = signal(false);

  signupForm: FormGroup = this.fb.group({
    email: [null, Validators.required],
    password: [null, Validators.required],
    passwordConfirm: [null, Validators.required],
    captchaToken: [null, this.moduleConfig.auth.captcha.type === "turnstile" ? Validators.required : null],
  }, {
    validators: [this._passwordMatchValidator()],
  });

  ngOnInit() {
    // Check for invite in query params
    const token = this.route.snapshot.queryParamMap.get('invite');
    if (token) {
      this.inviteToken.set(token);
      // Store in localStorage for post-verification flow
      localStorage.setItem(INVITE_TOKEN_KEY, token);
      // Fetch invite details
      this.loadInviteDetails(token);
    }
  }

  private async loadInviteDetails(token: string) {
    try {
      const details = await this.classService.getInviteByToken(token);
      if (details) {
        this.inviteDetails.set(details);
        // Pre-fill and lock email if available
        if (details.student_email) {
          this.signupForm.patchValue({ email: details.student_email });
          this.signupForm.get('email')?.disable();
          this.emailLocked.set(true);
        }
      }
    } catch (err) {
      console.error('Failed to load invite details:', err);
    }
  }

  private _passwordMatchValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const passwordMatch =
        control.value.password === control.value.passwordConfirm;
      return !passwordMatch ? { passwordMatch: true } : null;
    };
  }

  signupWithProvider(provider: string) {
    this.busy = true;
    // If there's an invite token, redirect back to join page after OAuth
    const token = this.inviteToken();
    const redirectTo = token
      ? `${window.location.origin}/join/${token}`
      : undefined;
    this.auth.signinWithProvider(provider as Provider, redirectTo).subscribe().add(() => this.busy = false);
  }

  async signUp() {
    if (this.signupForm.valid || (this.inviteToken() && this.signupForm.get('password')?.valid)) {
      this.error = undefined;
      this.busy = true;

      const token = this.inviteToken();
      const inviteDetails = this.inviteDetails();

      // If we have an invite token, use the special signup-with-invite flow
      // This skips email verification since they clicked a link from their email
      if (token && inviteDetails) {
        try {
          const { password } = this.signupForm.getRawValue();
          const result = await this.auth.signupWithInvite(
            token,
            password,
            inviteDetails.student_name
          );

          this.busy = false;

          if (result.success) {
            // Success! Redirect to student dashboard
            localStorage.removeItem(INVITE_TOKEN_KEY);
            this.router.navigate(['/student']);
          } else if (result.existing_user) {
            this.error = 'An account with this email already exists. Please sign in instead.';
          } else {
            this.error = result.error || 'Signup failed';
          }
        } catch (err: any) {
          this.busy = false;
          console.error("Signup failed:", err);
          this.error = err?.message || "Signup failed";
        }
        return;
      }

      // Regular teacher signup flow (requires email verification)
      const { email, password, captchaToken } = this.signupForm.value;
      this.auth.signUp(email, password, captchaToken).subscribe({
        next: () => {
          this.busy = false;
          this.showMailConfirm = true;
        },
        error: (err: Error) => {
          this.busy = false;
          console.error("Signup failed:", err);
          this.error = "Signup failed";
        },
      });
    }
  }
}
